import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Background,
  BackgroundVariant,
  Controls,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
  type NodeProps,
  type NodeTypes,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { userAppContext } from '@/context/Context';
import { resolveSearchSource } from '@/utils/allResultsSearch';
import { formatCanopyScore, type CanopyEdge, type CanopyTreeDatum } from '@/utils/canopyTree';
import { trackEvent } from '@/utils/analytics';
import { preserveBottomDetailForNextSearch } from '../utils/bottomDetailNavigation';
import { CanopyGeneCountBadge } from './CanopyGeneCountBadge';

const QUERY_COLOR = 'rgb(190, 0, 128)';
const PARENT_COLOR = '#0369a1';
const CHILD_COLOR = '#64748b';
const INDIRECT_COLOR = '#d97706';
const NODE_WIDTH = 230;
const NODE_HEIGHT = 118;
const X_GAP = 285;
const Y_GAP = 148;
const MAX_ROWS_PER_COLUMN = 3;
const VIEW_HEIGHT = 420;

type CanopyFlowNodeData = CanopyTreeDatum & {
  color: string;
  handles: {
    directSource: boolean;
    indirectSource: boolean;
    directTarget: boolean;
    indirectTarget: boolean;
  };
};

type CanopyFlowNode = Node<CanopyFlowNodeData, 'canopy'>;

function getNodeColor(node: CanopyTreeDatum): string {
  if (node.role === 'query') return QUERY_COLOR;
  const hasScore = typeof node.score === 'number' && Number.isFinite(node.score);
  if (!hasScore) return INDIRECT_COLOR;
  if (node.linkKind === 'parent') return PARENT_COLOR;
  return CHILD_COLOR;
}

const CanopyDagNode = memo(function CanopyDagNode({ data, selected }: NodeProps<CanopyFlowNode>) {
  const isInDisgenet = data.inDisgenet === true;
  const scorePercent =
    typeof data.score === 'number' && Number.isFinite(data.score)
      ? Math.max(0, Math.min(100, data.score * 100))
      : null;
  const directSourceTop = data.handles.indirectSource ? '32%' : '50%';
  const indirectSourceTop = data.handles.directSource ? '68%' : '50%';
  const directTargetTop = data.handles.indirectTarget ? '32%' : '50%';
  const indirectTargetTop = data.handles.directTarget ? '68%' : '50%';

  return (
    <div
      className={`rounded-lg px-3 py-2 shadow-sm ${
        isInDisgenet
          ? 'cursor-grab bg-white active:cursor-grabbing'
          : 'cursor-grab bg-slate-50 opacity-75 active:cursor-grabbing'
      }`}
      style={{
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
        boxSizing: 'border-box',
        border: `2px solid ${data.color}`,
        borderStyle: isInDisgenet ? 'solid' : 'dashed',
        boxShadow: selected
          ? `0 0 0 3px color-mix(in srgb, ${data.color} 20%, transparent)`
          : '0 2px 7px rgba(15, 23, 42, 0.08)',
      }}
      title={
        isInDisgenet
          ? `Drag to rearrange, or click to search ${data.label}`
          : `${data.label} is an ontology concept without a DisGeNET record`
      }
    >
      {data.handles.directTarget && (
        <Handle
          id="direct-target"
          type="target"
          position={Position.Left}
          style={{ top: directTargetTop, width: 8, height: 8, background: data.color }}
        />
      )}
      {data.handles.indirectTarget && (
        <Handle
          id="indirect-target"
          type="target"
          position={Position.Left}
          style={{ top: indirectTargetTop, width: 8, height: 8, background: INDIRECT_COLOR }}
        />
      )}
      <div
        className="h-7 overflow-hidden text-xs font-semibold leading-3.5 text-slate-800"
        title={data.label}
        style={{
          display: '-webkit-box',
          WebkitBoxOrient: 'vertical',
          WebkitLineClamp: 2,
        }}
      >
        {data.label}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2">
        <span className="truncate font-mono text-[0.65rem] text-slate-400">{data.id}</span>
        {typeof data.totalGenes === 'number' && Number.isFinite(data.totalGenes) && (
          <CanopyGeneCountBadge totalGenes={data.totalGenes} color={data.color} />
        )}
      </div>
      {scorePercent !== null && (
        <div className="mt-2" title={`Score ${formatCanopyScore(data.score)}`}>
          <div className="mb-1 flex items-center justify-between gap-2 text-[0.65rem] leading-none">
            <span className="font-semibold uppercase tracking-wider text-slate-400">Score</span>
            <span className="tabular-nums font-semibold text-slate-700">{formatCanopyScore(data.score)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <div
              className="h-full rounded-full"
              style={{ width: `${scorePercent}%`, background: data.color }}
            />
          </div>
        </div>
      )}
      {data.handles.directSource && (
        <Handle
          id="direct-source"
          type="source"
          position={Position.Right}
          style={{ top: directSourceTop, width: 8, height: 8, background: data.color }}
        />
      )}
      {data.handles.indirectSource && (
        <Handle
          id="indirect-source"
          type="source"
          position={Position.Right}
          style={{ top: indirectSourceTop, width: 8, height: 8, background: INDIRECT_COLOR }}
        />
      )}
    </div>
  );
});

const NODE_TYPES: NodeTypes = {
  canopy: CanopyDagNode as NodeTypes[string],
};

function calculateLevels(nodeIds: Set<string>, edges: CanopyEdge[], queryId: string): Map<string, number> {
  const levels = new Map<string, number>([[queryId, 0]]);
  const queue = [queryId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const currentLevel = levels.get(current)!;

    for (const edge of edges) {
      const adjacent =
        edge.parent === current
          ? { id: edge.child, level: currentLevel + 1 }
          : edge.child === current
            ? { id: edge.parent, level: currentLevel - 1 }
            : null;
      if (!adjacent || !nodeIds.has(adjacent.id) || levels.has(adjacent.id)) continue;
      levels.set(adjacent.id, adjacent.level);
      queue.push(adjacent.id);
    }
  }

  for (const id of nodeIds) {
    if (!levels.has(id)) levels.set(id, 1);
  }
  return levels;
}

export function buildCanopyDagElements(
  tree: CanopyTreeDatum,
  relationships: CanopyEdge[],
): { nodes: CanopyFlowNode[]; edges: Edge[] } {
  const dataNodes = [tree, ...(tree.children ?? [])];
  const nodeIds = new Set(dataNodes.map(node => node.id));
  const handlesByNode = new Map<string, CanopyFlowNodeData['handles']>();
  const getHandles = (id: string) => {
    const handles = handlesByNode.get(id) ?? {
      directSource: false,
      indirectSource: false,
      directTarget: false,
      indirectTarget: false,
    };
    handlesByNode.set(id, handles);
    return handles;
  };
  for (const edge of relationships) {
    if (!nodeIds.has(edge.parent) || !nodeIds.has(edge.child)) continue;
    const indirect = edge.parent !== tree.id && edge.child !== tree.id;
    const sourceHandles = getHandles(edge.parent);
    const targetHandles = getHandles(edge.child);
    if (indirect) {
      sourceHandles.indirectSource = true;
      targetHandles.indirectTarget = true;
    } else {
      sourceHandles.directSource = true;
      targetHandles.directTarget = true;
    }
  }
  const levels = calculateLevels(nodeIds, relationships, tree.id);
  const grouped = new Map<number, CanopyTreeDatum[]>();

  for (const node of dataNodes) {
    const level = levels.get(node.id) ?? 1;
    grouped.set(level, [...(grouped.get(level) ?? []), node]);
  }

  const sortedLevels = [...grouped.keys()].sort((a, b) => a - b);
  const nodes: CanopyFlowNode[] = [];
  let visualColumn = 0;

  for (const level of sortedLevels) {
    const group = [...(grouped.get(level) ?? [])].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
    const columnCount = Math.max(1, Math.ceil(group.length / MAX_ROWS_PER_COLUMN));

    for (let columnOffset = 0; columnOffset < columnCount; columnOffset += 1) {
      const columnNodes = group.slice(
        columnOffset * MAX_ROWS_PER_COLUMN,
        (columnOffset + 1) * MAX_ROWS_PER_COLUMN,
      );
      const yOffset = ((MAX_ROWS_PER_COLUMN - columnNodes.length) * Y_GAP) / 2;

      columnNodes.forEach((node, rowIndex) => {
        nodes.push({
          id: node.id,
          type: 'canopy',
          position: {
            x: (visualColumn + columnOffset) * X_GAP,
            y: yOffset + rowIndex * Y_GAP,
          },
          data: {
            ...node,
            color: getNodeColor(node),
            handles: getHandles(node.id),
          },
          ariaLabel:
            node.inDisgenet === true
              ? `Drag to rearrange, or click to search ${node.label}`
              : `${node.label} is an ontology concept without a DisGeNET record`,
          selectable: true,
          draggable: true,
          connectable: false,
        });
      });
    }

    visualColumn += columnCount;
  }

  const edges: Edge[] = relationships
    .filter(edge => nodeIds.has(edge.parent) && nodeIds.has(edge.child))
    .map(edge => {
      const indirect = edge.parent !== tree.id && edge.child !== tree.id;
      const color = indirect ? INDIRECT_COLOR : edge.child === tree.id ? PARENT_COLOR : CHILD_COLOR;
      return {
        id: `${edge.parent}->${edge.child}`,
        source: edge.parent,
        target: edge.child,
        sourceHandle: indirect ? 'indirect-source' : 'direct-source',
        targetHandle: indirect ? 'indirect-target' : 'direct-target',
        type: indirect ? 'smoothstep' : 'default',
        animated: false,
        interactionWidth: 24,
        markerEnd: { type: MarkerType.ArrowClosed, color, width: 16, height: 16 },
        style: {
          stroke: color,
          strokeWidth: indirect ? 2.25 : 2,
          strokeDasharray: indirect ? '5 4' : undefined,
        },
      };
    });

  return { nodes, edges };
}

type CanopyDagViewProps = {
  tree: CanopyTreeDatum;
  relationships: CanopyEdge[];
};

export function CanopyDagView({ tree, relationships }: CanopyDagViewProps) {
  const navigate = useNavigate();
  const { restrictSources, groups } = (userAppContext() ?? {}) as {
    restrictSources?: string | false | null;
    groups?: string[] | null;
  };
  const [hoveredEdgeId, setHoveredEdgeId] = useState<string | null>(null);
  const didDragRef = useRef(false);
  const elements = useMemo(() => buildCanopyDagElements(tree, relationships), [relationships, tree]);
  const [nodes, setNodes, onNodesChange] = useNodesState<CanopyFlowNode>(elements.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(elements.edges);

  useEffect(() => {
    setNodes(elements.nodes);
    setEdges(elements.edges);
  }, [elements, setEdges, setNodes]);

  const handleNodeClick = useCallback(
    (nodeId: string, inDisgenet: boolean) => {
      if (didDragRef.current) {
        didDragRef.current = false;
        return;
      }
      if (!inDisgenet) return;
      trackEvent({
        name: 'canopy_node_navigate',
        buttonId: 'canopy_dag_node_click',
        label: nodeId,
        page: 'search_results',
        section: 'canopy_dag',
        feature: 'canopy',
        eventCategory: 'canopy',
      });
      const source = resolveSearchSource({
        restrictSources: restrictSources || null,
        groups,
      });
      preserveBottomDetailForNextSearch();
      navigate({
        pathname: '/search',
        search: `?view=DISEASES&idents=${encodeURIComponent(nodeId)}&source=${source}&tab=GDA`,
      });
    },
    [groups, navigate, restrictSources],
  );
  const hoveredEdge = hoveredEdgeId ? edges.find(edge => edge.id === hoveredEdgeId) : undefined;
  const displayedNodes = useMemo(
    () =>
      nodes.map(node => ({
        ...node,
        style: {
          ...node.style,
          opacity: !hoveredEdge || node.id === hoveredEdge.source || node.id === hoveredEdge.target ? 1 : 0.3,
          transition: 'opacity 120ms ease',
        },
      })),
    [nodes, hoveredEdge],
  );
  const displayedEdges = useMemo(
    () =>
      edges.map(edge => {
        const isHovered = edge.id === hoveredEdgeId;
        return {
          ...edge,
          style: {
            ...edge.style,
            opacity: !hoveredEdgeId || isHovered ? 1 : 0.16,
            strokeWidth: isHovered ? 3.5 : edge.style?.strokeWidth,
            transition: 'opacity 120ms ease, stroke-width 120ms ease',
          },
        };
      }),
    [edges, hoveredEdgeId],
  );

  return (
    <div
      className="overflow-hidden rounded-lg bg-slate-50"
      style={{ height: VIEW_HEIGHT, minHeight: VIEW_HEIGHT }}
      role="img"
      aria-label="Linear disease relationship map"
      data-testid="canopy-dag"
      onPointerMove={event => {
        const target = event.target;
        if (!(target instanceof Element) || !target.closest('.react-flow__edge')) {
          setHoveredEdgeId(null);
        }
      }}
      onPointerLeave={() => setHoveredEdgeId(null)}
    >
      <ReactFlow
        nodes={displayedNodes}
        edges={displayedEdges}
        nodeTypes={NODE_TYPES}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStart={() => {
          didDragRef.current = true;
        }}
        onNodeClick={(_, node) => handleNodeClick(node.id, node.data.inDisgenet === true)}
        onEdgeMouseEnter={(_, edge) => setHoveredEdgeId(edge.id)}
        onEdgeMouseLeave={() => setHoveredEdgeId(null)}
        fitView
        fitViewOptions={{ padding: 0.12, minZoom: 0.5, maxZoom: 1 }}
        minZoom={0.35}
        maxZoom={1.5}
        nodesDraggable
        nodesConnectable={false}
        elementsSelectable
        zoomOnDoubleClick={false}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#dbe3ed" />
        <Controls
          className="canopy-dag-controls"
          showInteractive={false}
          style={{ border: 'none', boxShadow: 'none', background: 'transparent' }}
        />
      </ReactFlow>
      <style>{`
        .canopy-dag-controls {
          border: none !important;
          box-shadow: none !important;
          background: transparent !important;
        }
        .canopy-dag-controls .react-flow__controls-button {
          border: none !important;
          box-shadow: none !important;
        }
      `}</style>
    </div>
  );
}
